import React, { useContext, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Box, Button, Checkbox, CssBaseline, Divider, FormControl,
  FormControlLabel, FormLabel, IconButton, InputAdornment,
  Link, Stack, TextField, ThemeProvider, Typography,
} from '@mui/material';
import VisibilityRoundedIcon      from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon   from '@mui/icons-material/VisibilityOffRounded';
import EcoRoundedIcon             from '@mui/icons-material/SpaRounded';
import axios from 'axios';
import { AuthContext } from '../components/signin/AuthContext';
import Auth              from '../components/signin/auth/auth';
import ForgotPassword    from '../components/signin/ForgotPassword';
import { v3Theme }       from './theme';
import ThemePicker       from './components/ThemePicker';
import gglogo            from '../assets/GG logo 1.png';

/* CSS animations */
const ANIM_CSS = `
@keyframes bgShift {
  0%   { background-position: 0% 50%;   }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%;   }
}
@keyframes floatCard {
  0%,100% { transform: translateY(0px);   }
  50%      { transform: translateY(-6px); }
}
@keyframes fadeIn {
  from { opacity:0; transform:translateY(24px); }
  to   { opacity:1; transform:translateY(0);    }
}
@keyframes particleDrift {
  0%   { transform: translateY(0) translateX(0) scale(1); opacity:0.6; }
  50%  { transform: translateY(-60px) translateX(20px) scale(1.1); opacity:0.3; }
  100% { transform: translateY(-120px) translateX(-10px) scale(0.8); opacity:0; }
}
`;

function Particle({ style }) {
  return (
    <Box sx={{
      position: 'absolute',
      width: 4, height: 4,
      borderRadius: '50%',
      background: 'rgba(118,196,66,0.5)',
      animation: 'particleDrift 6s ease-in-out infinite',
      ...style,
    }} />
  );
}

export default function V3SignIn() {
  const navigate = useNavigate();
  const { token, setToken } = useContext(AuthContext);
  const [showPw,    setShowPw]    = useState(false);
  const [pwError,   setPwError]   = useState('');
  const [emailVal,  setEmailVal]  = useState('');
  const [pwVal,     setPwVal]     = useState('');
  const [forgotOpen, setForgot]   = useState(false);

  useEffect(() => {
    if (token) navigate('/v3/dashboard');
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pwVal || pwVal.length < 6) {
      setPwError('Mindestens 6 Zeichen erforderlich.');
      return;
    }
    setPwError('');
    try {
      const res = await axios.post('/login_password', {
        username: emailVal, password: pwVal, remember: true,
      });
      setToken(res.data.access_token);
      localStorage.setItem('authenticated', 'true');
      navigate('/v3/dashboard');
    } catch (err) {
      setPwError('Anmeldung fehlgeschlagen. Bitte prüfe deine Eingaben.');
    }
  };

  if (localStorage.getItem('authenticated') === 'true' && token) {
    return <Navigate to="/v3/dashboard" replace />;
  }

  const particles = [
    { top: '15%', left: '8%',  animationDelay: '0s',   animationDuration: '7s'  },
    { top: '70%', left: '12%', animationDelay: '1.5s', animationDuration: '5.5s'},
    { top: '40%', left: '85%', animationDelay: '0.8s', animationDuration: '6.5s'},
    { top: '80%', left: '75%', animationDelay: '2s',   animationDuration: '8s'  },
    { top: '25%', left: '55%', animationDelay: '3s',   animationDuration: '5s'  },
    { top: '60%', left: '40%', animationDelay: '1s',   animationDuration: '7.5s'},
    { top: '10%', left: '65%', animationDelay: '2.5s', animationDuration: '6s'  },
    { top: '90%', left: '35%', animationDelay: '0.5s', animationDuration: '5.8s'},
  ];

  return (
    <ThemeProvider theme={v3Theme}>
      <CssBaseline />
      <style>{ANIM_CSS}</style>

      {/* Full page background */}
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(-45deg,#060d07,#0a1a0a,#081408,#040b04)',
        backgroundSize: '400% 400%',
        animation: 'bgShift 14s ease infinite',
        p: 2,
      }}>
        <ThemePicker sx={{ position: 'absolute', top: 16, right: 16, zIndex: 2 }} />
        {/* Ambient radial glow */}
        <Box sx={{
          position: 'absolute', top: '20%', left: '15%',
          width: 500, height: 500, borderRadius: '50%',
          background: 'radial-gradient(circle,rgba(118,196,66,0.12) 0%,transparent 70%)',
          filter: 'blur(40px)',
          pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', bottom: '10%', right: '10%',
          width: 400, height: 400, borderRadius: '50%',
          background: 'radial-gradient(circle,rgba(52,208,88,0.08) 0%,transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }} />

        {/* Floating particles */}
        {particles.map((p, i) => <Particle key={i} style={p} />)}

        {/* Sign-in card */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 420,
            animation: 'fadeIn 0.6s cubic-bezier(0.22,1,0.36,1) both',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              background: 'rgba(12,24,12,0.85)',
              border: '1px solid rgba(118,196,66,0.18)',
              borderRadius: '24px',
              backdropFilter: 'blur(24px)',
              boxShadow: '0 32px 80px rgba(0,0,0,0.6), inset 0 1px 0 rgba(118,196,66,0.1)',
              p: { xs: 3, sm: 4 },
            }}
          >
            {/* Logo */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: '14px',
                background: 'linear-gradient(135deg,#76c442 0%,#3a7d20 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(118,196,66,0.4)',
              }}>
                <EcoRoundedIcon sx={{ color: '#fff', fontSize: 26 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>
                  Growr
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                  Grow smarter
                </Typography>
              </Box>
              <Box sx={{ ml: 'auto' }}>
                <img src={gglogo} alt="GG Logo" style={{ height: 36, opacity: 0.85 }} />
              </Box>
            </Stack>

            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Willkommen zurück
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mb: 3 }}>
              Melde dich an, um dein Grow-Setup zu verwalten.
            </Typography>

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={2}>
                <FormControl>
                  <FormLabel sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.75, fontSize: '0.82rem' }}>
                    E-Mail / Benutzername
                  </FormLabel>
                  <TextField
                    id="email" type="text" name="email" autoComplete="email"
                    placeholder="deine@email.com"
                    value={emailVal}
                    onChange={e => setEmailVal(e.target.value)}
                    fullWidth required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(255,255,255,0.04)',
                        borderRadius: '12px',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                        '&:hover fieldset': { borderColor: 'rgba(118,196,66,0.4)' },
                        '&.Mui-focused fieldset': { borderColor: '#76c442' },
                      },
                    }}
                  />
                </FormControl>

                <FormControl>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                    <FormLabel sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem' }}>
                      Passwort
                    </FormLabel>
                    <Link
                      component="button" type="button" variant="caption"
                      onClick={() => setForgot(true)}
                      sx={{ color: '#76c442', '&:hover': { color: '#8dd55a' } }}
                    >
                      Vergessen?
                    </Link>
                  </Stack>
                  <TextField
                    id="password" name="password" type={showPw ? 'text' : 'password'}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    value={pwVal}
                    onChange={e => setPwVal(e.target.value)}
                    error={!!pwError}
                    helperText={pwError}
                    fullWidth required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowPw(p => !p)} sx={{ color: 'rgba(255,255,255,0.4)' }}>
                            {showPw ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(255,255,255,0.04)',
                        borderRadius: '12px',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                        '&:hover fieldset': { borderColor: 'rgba(118,196,66,0.4)' },
                        '&.Mui-focused fieldset': { borderColor: '#76c442' },
                      },
                    }}
                  />
                </FormControl>

                <FormControlLabel
                  control={<Checkbox size="small" sx={{ color: 'rgba(255,255,255,0.3)', '&.Mui-checked': { color: '#76c442' } }} />}
                  label={<Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>Angemeldet bleiben</Typography>}
                />

                <ForgotPassword open={forgotOpen} handleClose={() => setForgot(false)} />

                <Button
                  type="submit" fullWidth variant="contained"
                  sx={{
                    py: 1.5, fontSize: '1rem', fontWeight: 700,
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg,#76c442 0%,#559e2e 100%)',
                    boxShadow: '0 8px 24px rgba(118,196,66,0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg,#8dd55a 0%,#65ae3e 100%)',
                      boxShadow: '0 10px 28px rgba(118,196,66,0.55)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.18s ease',
                  }}
                >
                  Anmelden
                </Button>
              </Stack>
            </Box>

            <Divider sx={{ my: 2.5, borderColor: 'rgba(255,255,255,0.08)', '&::before,&::after': { borderColor: 'rgba(255,255,255,0.08)' } }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', px: 1 }}>oder</Typography>
            </Divider>

            <Box sx={{ mb: 2 }}>
              <Auth />
            </Box>

            <Typography variant="body2" sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.45)' }}>
              Noch kein Konto?{' '}
              <Link href="/signup" sx={{ color: '#76c442', fontWeight: 600, '&:hover': { color: '#8dd55a' } }}>
                Registrieren
              </Link>
            </Typography>
          </Box>

          {/* Bottom hint */}
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2, color: 'rgba(255,255,255,0.2)' }}>
            Growr v3 — Modern Grow Management
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
